import { CustomerPart, OnlinePartDetails } from "@ncs/ncs-api/portal-apps"
import { useScreenSizeMatch } from "@ncs/web-legos/util"
import { DetailsBlock } from "./DetailsBlock"
import PartDetailTabs from "./PartDetailTabs"
import { css, Theme } from "@emotion/react"
import { Box, Flag, Heading, Icon, useIsFeatureFlagOn } from "@ncs/web-legos/components"
import { ReactNode } from "react"
export interface PartDetailTabsProps {
	partDetails?: OnlinePartDetails
	initialPart?: CustomerPart
	selectedChildPart?: SelectedChildPart | undefined
	selectedPartDetails?: OnlinePartDetails | undefined
}

export interface PartDocument {
	presignUrl?: string
	presign_url: string
	extension?: string
	document_type_name?: string
	description?: string
}

export interface SelectedChildPart {
	id: string
	description: string | null
	flatShippingRate: string | null
	imageUrl: string | null
	childImage: string | null
	isActive: boolean
	isShippedFlatRate: boolean
	netPrice: number | null
	clearanceItemDiscount: number | null
	part: {
		id: string
		description: string
		partNumber: string
		unitOfMeasure: string
		documents?: PartDocument[]
		height?: number | null
		length?: number | null
		width?: number | null
		weight?: number | null
		packageSizeInGallons?: number | null
	} | null
}

interface DocumentItem {
	downloadURL: string | undefined
	documentTypeName: string
	description: string
}

interface DocumentsSectionProps {
	items: DocumentItem[]
}

const PartDetails: React.FC<PartDetailTabsProps> = ({
	partDetails,
	initialPart,
	selectedChildPart,
	selectedPartDetails,
}) => {
	const isPDPDocumentsEnabled = useIsFeatureFlagOn(Flag.VITE_PDP_DOCUMENTS_FLAG)

	const documentArray: PartDocument[] =
		selectedChildPart?.part?.documents?.length ?
			selectedChildPart.part.documents
		:	(partDetails?.part?.documents ?? [])

	const brandName = (partDetails?.brands ?? []).map((brand) => brand.brandName).join(", ")

	const categories = (partDetails?.categories ?? [])
		.map((category) => category.categoryName)
		.join(", ")

	const manufacturer = (partDetails?.manufacturers ?? [])
		.map((i) => i.manufacturerName)
		.join(", ")

	const usedOn = (partDetails?.platforms ?? [])
		.map((platform) => platform.platformName)
		.join(", ")

	const isMobile = useScreenSizeMatch("xs")

	const documents = (documentArray ?? []).map((document) => ({
		extension: document?.extension ?? "",
		documentTypeName: document?.document_type_name ?? "",
		description: document?.description ?? "",
		downloadURL: document?.presignUrl ?? "",
	}))

	const areThereDocuments = documents.length > 0 ? true : false

	if (!partDetails) return null

	// priority to selectedPartDetails if a child is selected, otherwise parent partDetails
	const activePartDetails = selectedPartDetails ?? partDetails

	// helper func to get weight/dimensions and fallback to parent part
	const getPartValue = (childValue: any, parentValue: any) => {
		return childValue ?? parentValue
	}

	// selectedPartDetails.part -> partDetails.part
	const effectivePart = {
		height: getPartValue(activePartDetails.part?.height, partDetails.part?.height),
		length: getPartValue(activePartDetails.part?.length, partDetails.part?.length),
		width: getPartValue(activePartDetails.part?.width, partDetails.part?.width),
		weight: getPartValue(activePartDetails.part?.weight, partDetails.part?.weight),
		packageSizeInGallons: getPartValue(
			activePartDetails.part?.packageSizeInGallons,
			partDetails.part?.packageSizeInGallons
		),
	}
	const DetailItem = ({
		label,
		value,
	}: {
		label: ReactNode
		value?: string | number | null
	}) => {
		if (value === null || value === undefined) return null
		return (
			<>
				<Heading variant="h3" css={styleTabHeader}>
					{label}
				</Heading>
				<div css={styleDimensionValue}>{value}</div>
			</>
		)
	}

	const DocumentsSection: React.FC<DocumentsSectionProps> = ({ items }) => {
		return (
			<Box css={documentContainer}>
				{items.map((item) => {
					const isDocument = item.documentTypeName.toLowerCase() === "document"
					const icon: ReactNode =
						!isDocument ? <Icon icon="file-pdf" /> : <Icon icon="circle-play" />
					const title = !isDocument ? "Document" : "Video"

					return (
						<div key={item.description}>
							<a href={item.downloadURL} css={titleRow}>
								{icon}
								<h3>{title}</h3>
							</a>

							<p>{item.description}</p>
						</div>
					)
				})}
			</Box>
		)
	}

	const NoDocuments = () => (
		<div css={noDcouments}>
			<h4>No documents available for this product yet</h4>
			<p>We’re working on adding manuals, datasheets, and other resources soon.</p>
		</div>
	)

	const technicalInformationTabs = [
		{
			navLabel: "Product Details",
			component: (
				<>
					<DetailItem label="Description" value={initialPart?.description} />
					<DetailItem label="Brand" value={brandName} />
					<DetailItem label="Manufacturer" value={manufacturer} />
					<DetailItem label="Used On" value={usedOn} />
					<DetailItem label="Product Type" value={categories} />
				</>
			),
		},
		{
			navLabel: "Weight & Dimension",
			component: (
				<Box display="flex">
					<div css={detailColumn}>
						<DetailItem
							label="Height"
							value={
								effectivePart.height != null ? `${effectivePart.height}"` : null
							}
						/>
						<DetailItem
							label="Length"
							value={
								effectivePart.length != null ? `${effectivePart.length}"` : null
							}
						/>
						<DetailItem
							label="Width"
							value={effectivePart.width != null ? `${effectivePart.width}"` : null}
						/>
					</div>
					<div>
						<DetailItem
							label="Weight"
							value={
								effectivePart.weight != null ? `${effectivePart.weight} lbs` : null
							}
						/>
						<DetailItem
							label="Gallons"
							value={effectivePart.packageSizeInGallons ?? null}
						/>
					</div>
				</Box>
			),
		},
		...(isPDPDocumentsEnabled ?
			[
				{
					navLabel: "Documents",
					component:
						areThereDocuments ?
							<DocumentsSection items={documents} />
						:	<NoDocuments />,
				},
			]
		:	[]),
	]

	const technicalInformationBlock = [
		{
			title: "Product Details",
			content: [
				{ label: "Description", value: initialPart?.description },
				{ label: "Brand", value: brandName },
				{ label: "Manufacturer", value: manufacturer },
				{ label: "Used On", value: usedOn },
				{ label: "Product Type", value: categories },
			],
		},
		{
			title: "Weight & Dimension",
			content: [
				{
					label: "Height",
					value: effectivePart.height ? `${effectivePart.height}"` : null,
				},
				{
					label: "Length",
					value: effectivePart.length ? `${effectivePart.length}"` : null,
				},
				{
					label: "Width",
					value: effectivePart.width ? `${effectivePart.width}"` : null,
				},
				{
					label: "Weight",
					value: effectivePart.weight ? `${effectivePart.weight} lbs` : null,
				},
				{
					label: "Gallons",
					value: effectivePart.packageSizeInGallons ?? null,
				},
			],
		},
		...(isPDPDocumentsEnabled ?
			[
				{
					title: "Documents",
					content: [
						{
							label:
								areThereDocuments ?
									<DocumentsSection items={documents} />
								:	<NoDocuments />,
							value: "",
						},
					],
				},
			]
		:	[]),
	]

	return isMobile ?
			<DetailsBlock technicalInformation={technicalInformationBlock} />
		:	<>
				<PartDetailTabs panels={technicalInformationTabs} />
			</>
}

const styleTabHeader = css`
	color: #111827;
	font-size: 1rem;
	font-weight: 700;
`

const styleDimensionValue = css`
	margin-top: 1rem;
	margin-bottom: 32px;
`

const detailColumn = (theme: Theme, documents?: string) => css`
	width: ${documents ? "100%" : "18rem"};
`

const documentContainer = (theme: Theme) => css`
	display: flex;
	flex-direction: column;

	> div {
		display: flex;
		flex-direction: row;
		align-items: center;
		padding: 1.5rem 0;
		border-bottom: 1px solid #e5e7eb;

		${theme.breakpoints.down("xs")} {
			flex-direction: column;
			align-items: flex-start;

			p {
				margin-left: 3rem;
				font-weight: 400;
			}
		}

		&:last-of-type {
			border-bottom: 0;
		}
	}
`

const titleRow = (theme: Theme) => css`
	width: 100%;
	max-width: 100%;
	display: flex;
	align-items: center;
	text-decoration: none;

	${theme.breakpoints.down("xs")} {
		margin-bottom: 1rem;
	}

	h3 {
		font-size: 1rem;
		color: #0b75e1;
		font-weight: 700;
		margin-left: 1rem;
	}
`

const noDcouments = css`
	text-align: center;
	h4 {
		text-transform: none;
		font-size: 16px;
		font-weight: 700;
		color: #6b7280;
	}

	p {
		font-size: 14px;
		color: #6b7280;
	}
`

export default PartDetails
